import {
  formatCurrency,
  formatPriceUnit,
  formatUnitAbbreviation,
} from '@app/utils/formatting'
import { List, Record } from 'immutable'
import { AssociatedContact, IAssociatedContact } from './AssociatedContact'
import { AssociatedProperty, IAssociatedProperty } from './AssociatedProperty'
import { Attachment, IAttachment } from './Attachment'
import { ListingStatus, ListingType, PriceUnits } from './enums'
import {
  IListingFinancialDetails,
  ListingFinancialDetails,
} from './ListingFinancialDetails'
import { IListingLeaseData, ListingLeaseData } from './ListingLeaseData'
import { IListingProgress, ListingProgress } from './ListingProgress'
import { IListingSaleData, ListingSaleData } from './ListingSaleData'
import { ILot, Lot } from './Lot'
import {
  IOnlineListingService,
  OnlineListingService,
} from './OnlineListingService'
import { IProperty, Property } from './Property'
import { ISpace, Space } from './Space'
import { IListingProgressV2, ListingProgressV2 } from './ListingProgressV2'

export interface IListing {
  readonly activeDate?: number
  readonly adjoiningPropertyOwners: List<IAssociatedContact>
  readonly associatedProperty: IAssociatedProperty
  readonly attachments: List<IAttachment>
  readonly creator: string
  readonly expirationDate?: number
  readonly filePositionNo: string
  readonly financialData: IListingFinancialDetails
  readonly flyer?: IAttachment
  readonly flyerId: string
  readonly highlights: List<string>
  readonly id: string
  readonly isFeatured: boolean
  readonly isInvestmentProperty: boolean
  readonly leaseData?: IListingLeaseData
  readonly listingAgents: List<IAssociatedContact>
  readonly listingServices: List<IOnlineListingService>
  readonly lot?: ILot
  readonly lotId: string
  readonly marketingPackage?: IAttachment
  readonly marketingPackageId: string
  readonly noOfSigns: number
  readonly notHBListing: boolean
  readonly photos: List<IAttachment>
  readonly progress: IListingProgress
  readonly progressV2: IListingProgressV2
  readonly property: IProperty
  readonly propertyDescription: string
  readonly referral: boolean
  readonly saleData?: IListingSaleData
  readonly showOnWebsite: boolean
  readonly signageNotes: string
  readonly space?: ISpace
  readonly spaceId: string
  readonly status: ListingStatus
  readonly thumbnailImage?: IAttachment
  readonly thumbnailImageId: string
  readonly title: string
  readonly topProspects: List<IAssociatedContact>
  readonly type: ListingType
  readonly videoLinks: List<string>
}

const ListingRecord = Record({
  activeDate: undefined,
  adjoiningPropertyOwners: List<AssociatedContact>(),
  associatedProperty: new AssociatedProperty(),
  attachments: List<Attachment>(),
  creator: '',
  expirationDate: undefined,
  filePositionNo: '',
  financialData: new ListingFinancialDetails(),
  flyer: undefined,
  flyerId: undefined,
  highlights: List<string>(),
  id: '',
  isFeatured: false,
  isInvestmentProperty: false,
  leaseData: undefined,
  listingAgents: List<AssociatedContact>(),
  listingServices: List<OnlineListingService>(),
  lot: undefined,
  lotId: '',
  marketingPackage: undefined,
  marketingPackageId: undefined,
  noOfSigns: 0,
  notHBListing: false,
  photos: List<Attachment>(),
  progress: new ListingProgress(),
  progressV2: new ListingProgressV2(),
  property: new Property(),
  propertyDescription: '',
  quickAdd: false,
  referral: false,
  saleData: new ListingSaleData(),
  showOnWebsite: false,
  signageNotes: '',
  space: undefined,
  spaceId: '',
  status: ListingStatus.Active,
  thumbnailImage: undefined,
  thumbnailImageId: '',
  title: '',
  topProspects: List<AssociatedContact>(),
  type: ListingType.Sale,
  videoLinks: List<string>(),
})

export class Listing extends ListingRecord implements IListing {
  public static fromJS(initialState?: Partial<IListing>) {
    if (!initialState) {
      return new Listing()
    }

    const {
      adjoiningPropertyOwners,
      associatedProperty,
      attachments,
      financialData,
      flyer,
      highlights,
      leaseData,
      listingAgents,
      listingServices,
      lot,
      marketingPackage,
      photos,
      progress,
      progressV2,
      property,
      saleData,
      space,
      thumbnailImage,
      topProspects,
      videoLinks,
      ...rest
    } = initialState

    const listing = new Listing({
      adjoiningPropertyOwners: Array.isArray(adjoiningPropertyOwners)
        ? List(adjoiningPropertyOwners.map((a) => AssociatedContact.fromJS(a)))
        : List<AssociatedContact>(),
      associatedProperty: associatedProperty
        ? AssociatedProperty.fromJS(associatedProperty)
        : new AssociatedProperty(),
      attachments: Array.isArray(attachments)
        ? List(attachments.map((a) => Attachment.fromJS(a)))
        : List<Attachment>(),
      financialData: financialData
        ? ListingFinancialDetails.fromJS(financialData)
        : new ListingFinancialDetails(),
      flyer: flyer ? Attachment.fromJS(flyer) : undefined,
      highlights: List(highlights || []),
      leaseData: leaseData
        ? ListingLeaseData.fromJS(leaseData)
        : new ListingLeaseData(),
      listingAgents: Array.isArray(listingAgents)
        ? List(listingAgents.map((l) => AssociatedContact.fromJS(l)))
        : List<AssociatedContact>(),
      listingServices: Array.isArray(listingServices)
        ? List(listingServices.map((o) => OnlineListingService.fromJS(o)))
        : List<OnlineListingService>(),
      lot: lot ? Lot.fromJS(lot) : undefined,
      marketingPackage: marketingPackage
        ? Attachment.fromJS(marketingPackage)
        : undefined,
      photos: Array.isArray(photos)
        ? List(photos.map((p) => Attachment.fromJS(p)))
        : List<Attachment>(),
      progress: progress
        ? ListingProgress.fromJS(progress)
        : new ListingProgress(),
      progressV2: progressV2
        ? ListingProgressV2.fromJS(progressV2)
        : new ListingProgressV2(),
      property: property ? Property.fromJS(property) : undefined,
      saleData: saleData
        ? ListingSaleData.fromJS(saleData)
        : new ListingSaleData(),
      space: space ? Space.fromJS(space) : undefined,
      thumbnailImage: thumbnailImage
        ? Attachment.fromJS(thumbnailImage)
        : undefined,
      topProspects: Array.isArray(topProspects)
        ? List(topProspects.map((t) => AssociatedContact.fromJS(t)))
        : List<AssociatedContact>(),
      videoLinks: List(videoLinks || []),
      ...rest,
    })

    return listing
  }

  public static getListingPrice(listing: IListing) {
    if (!listing) {
      return ''
    }

    let output = ''
    if (
      listing.type === ListingType.Sale ||
      listing.type === ListingType.Development
    ) {
      if (!listing.saleData || !listing.saleData.askingPrice) {
        return ''
      }

      if (listing.saleData.askingPrice.confidential) {
        return 'Contact Broker for Price'
      }

      const roundedTo = listing.saleData.askingPrice.amount % 1 !== 0 ? 2 : 0

      output = formatCurrency(listing.saleData.askingPrice.amount, roundedTo)
      if (listing.saleData.askingPrice.units !== PriceUnits.Total) {
        let units = formatPriceUnit(listing.saleData.askingPrice.units)
        units = formatUnitAbbreviation(units)
        output += ` ${units}`
      }
    } else if (listing.type === ListingType.Lease) {
      if (!listing.leaseData || !listing.leaseData.rentalRate) {
        return ''
      }

      const roundedTo = listing.leaseData.rentalRate.rate % 1 !== 0 ? 2 : 0
      output = formatCurrency(listing.leaseData.rentalRate.rate, 2)
      let units = formatPriceUnit(listing.leaseData.rentalRate.units)
      units = formatUnitAbbreviation(units)
      output += ` ${units}`
    }

    return output
  }

  public readonly activeDate?: number
  public readonly adjoiningPropertyOwners: List<AssociatedContact>
  public readonly associatedProperty: AssociatedProperty
  public readonly attachments: List<Attachment>
  public readonly creator: string
  public readonly expirationDate?: number
  public readonly filePositionNo: string
  public readonly financialData: ListingFinancialDetails
  public readonly flyer?: Attachment
  public readonly flyerId: string
  public readonly highlights: List<string>
  public readonly id: string
  public readonly isFeatured: boolean
  public readonly isInvestmentProperty: boolean
  public readonly leaseData: ListingLeaseData
  public readonly listingAgents: List<AssociatedContact>
  public readonly listingServices: List<OnlineListingService>
  public readonly lot?: Lot
  public readonly lotId: string
  public readonly marketingPackage?: Attachment
  public readonly marketingPackageId: string
  public readonly noOfSigns: number
  public readonly notHBListing: boolean
  public readonly photos: List<Attachment>
  public readonly progress: ListingProgress
  public readonly progressV2: IListingProgressV2
  public readonly property: Property
  public readonly propertyDescription: string
  public readonly quickAdd: boolean
  public readonly referral: boolean
  public readonly saleData: ListingSaleData
  public readonly showOnWebsite: boolean
  public readonly signageNotes: string
  public readonly space?: Space
  public readonly spaceId: string
  public readonly status: ListingStatus
  public readonly thumbnailImage?: Attachment
  public readonly thumbnailImageId: string
  public readonly title: string
  public readonly topProspects: List<AssociatedContact>
  public readonly type: ListingType
  public readonly videoLinks: List<string>

  constructor(props?: Partial<Listing>) {
    props ? super(props) : super()
  }
}
